// src/features/validation/components/DossierPreviewCanvas.jsx
import React from 'react';
import { Box, Typography, Stack, Chip, Divider, Avatar } from '@mui/material';
import {
    FlightTakeoff,
    Hotel,
    HealthAndSafety,
    CalendarMonth,
    AirplaneTicket,
    MeetingRoom,
    Public,
    Tag,
    Person,
} from '@mui/icons-material';
import { formatDate } from '../../../utils/utilis';

// ── Configuration Visuelle ──────────────────────────────────────────────────
const PRESTATION_META = {
    BILLET: { icon: FlightTakeoff, color: '#4f8ef7', bg: 'rgba(79,142,247,0.08)', label: 'Billet' },
    RESERVATION_HOTEL: { icon: Hotel, color: '#c9a84c', bg: 'rgba(201,168,76,0.08)', label: 'Hôtel' },
    ASSURANCE: { icon: HealthAndSafety, color: '#6dd9a8', bg: 'rgba(109,217,168,0.08)', label: 'Assurance' },
};

// ── Ligne d'information Adaptative ──────────────────────────────────────────
function InfoRow({ icon: Icon, label, value }) {
    if (!value) return null;
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{ mt: 0.2, flexShrink: 0 }}>
                <Icon sx={{ fontSize: 13, color: 'rgba(26,26,46,0.35)' }} />
            </Box>
            <Box>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(26,26,46,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.15 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1a2e', lineHeight: 1.3 }}>
                    {value}
                </Typography>
            </Box>
        </Stack>
    );
}

// ── Sections Détails (Responsive Grid) ──────────────────────────────────────
const DetailGrid = ({ children }) => (
    <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: { xs: 2.5, md: 2 },
        mt: 2, pt: 2,
        borderTop: '1px solid rgba(26,26,46,0.06)'
    }}>
        {children}
    </Box>
);

function BilletDetails({ p }) {
    const seg = p.segments?.[0];
    return (
        <>
            <DetailGrid>
                <InfoRow icon={AirplaneTicket} label="PNR" value={p.pnr} />
                <InfoRow icon={Tag} label="N° Billet" value={p.numeroBillet} />
                <InfoRow icon={FlightTakeoff} label="Itinéraire" value={p.typeItineraire?.replace('_', ' ')} />
                <InfoRow icon={Public} label="Escale" value={p.aescale ? 'Oui' : 'Non'} />
            </DetailGrid>
            {seg && (
                <Box sx={{ mt: 2, p: 2, borderRadius: '12px', bgcolor: 'rgba(79,142,247,0.04)', border: '1px solid rgba(79,142,247,0.1)' }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
                        <Box sx={{ textAlign: 'center', minWidth: 52 }}>
                            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#1a1a2e' }}>{seg.codeAeroportDepart}</Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(26,26,46,0.4)' }}>{seg.dateHeureDepart?.split('T')[1]?.substring(0, 5)}</Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ height: 1, flexGrow: 1, bgcolor: 'rgba(79,142,247,0.25)' }} />
                            <FlightTakeoff sx={{ fontSize: 14, color: '#4f8ef7' }} />
                            <Box sx={{ height: 1, flexGrow: 1, bgcolor: 'rgba(79,142,247,0.25)' }} />
                        </Box>
                        <Box sx={{ textAlign: 'center', minWidth: 52 }}>
                            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#1a1a2e' }}>{seg.codeAeroportArrivee}</Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(26,26,46,0.4)' }}>{seg.dateHeureArrivee?.split('T')[1]?.substring(0, 5)}</Typography>
                        </Box>
                    </Stack>
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(26,26,46,0.45)', textAlign: 'center' }}>
                        {seg.compagnieAerienne} · {seg.numeroVol} · {seg.classeReservation}
                    </Typography>
                </Box>
            )}
        </>
    );
}

function HotelDetails({ p }) {
    return (
        <DetailGrid>
            <InfoRow icon={Hotel} label="Hôtel" value={p.nomHotel} />
            <InfoRow icon={Public} label="Ville" value={p.ville} />
            <InfoRow icon={CalendarMonth} label="Check-in" value={p.dateCheckIn} />
            <InfoRow icon={CalendarMonth} label="Check-out" value={p.dateCheckOut} />
            <InfoRow icon={MeetingRoom} label="Chambre" value={p.typeChambre} />
            <InfoRow icon={Tag} label="Pension" value={p.typePension} />
        </DetailGrid>
    );
}

function AssuranceDetails({ p }) {
    return (
        <DetailGrid>
            <InfoRow icon={HealthAndSafety} label="Compagnie" value={p.compagnieAssurance} />
            <InfoRow icon={Tag} label="N° Police" value={p.numeroPolice} />
            <InfoRow icon={CalendarMonth} label="Début" value={p.dateDebutCouverture} />
            <InfoRow icon={CalendarMonth} label="Fin" value={p.dateFinCouverture} />
        </DetailGrid>
    );
}

// ── Carte Prestation ────────────────────────────────────────────────────────
function PrestationCard({ prestation }) {
    const meta = PRESTATION_META[prestation.type] || PRESTATION_META.BILLET;
    const Icon = meta.icon;

    return (
        <Box sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: '16px',
            bgcolor: 'white',
            border: '1px solid rgba(26,26,46,0.06)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ width: 42, height: 42, borderRadius: '11px', bgcolor: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon sx={{ fontSize: 18, color: meta.color }} />
                    </Box>
                    <Box>
                        <Chip label={meta.label} size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: meta.bg, color: meta.color, mb: 0.4 }} />
                        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(26,26,46,0.4)' }}>
                            {prestation.referenceCode || 'Réf. interne'}
                        </Typography>
                    </Box>
                </Stack>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1a1a2e' }}>
                    {prestation.prixVente?.toLocaleString()} FCFA
                </Typography>
            </Stack>

            {prestation.type === 'BILLET' && <BilletDetails p={prestation} />}
            {prestation.type === 'RESERVATION_HOTEL' && <HotelDetails p={prestation} />}
            {prestation.type === 'ASSURANCE' && <AssuranceDetails p={prestation} />}
        </Box>
    );
}

// ── Composant Principal ─────────────────────────────────────────────────────
export function DossierPreviewCanvas({ dossier }) {
    if (!dossier) return null;

    const initials = dossier.client?.nomComplet?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() ?? '??';

    return (
        <Box sx={{ width: '100%', pb: 4 }}>

            {/* 1. Carte Header Client (Responsive) */}
            <Box sx={{
                borderRadius: '24px', bgcolor: '#1a1a2e', p: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden', color: 'white'
            }}>
                {/* Effet décoratif or */}
                <Box sx={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.12) 0%, transparent 70%)' }} />

                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={3}>
                    <Stack direction="row" spacing={2.5} alignItems="center">
                        <Box sx={{ width: 60, height: 60, borderRadius: '16px', bgcolor: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ color: '#c9a84c', fontWeight: 800, fontSize: '1.2rem' }}>{initials}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Client Principal</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{dossier.client?.nomComplet}</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>{dossier.client?.email}</Typography>
                        </Box>
                    </Stack>
                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography sx={{ color: '#c9a84c', fontWeight: 800, fontSize: '1.1rem' }}>#{dossier.referenceCode}</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>Créé le {formatDate(dossier.dateCreation)}</Typography>
                    </Box>
                </Stack>

                <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2.5 }}>
                    {[
                        { label: 'Agent', value: dossier.agentUsername },
                        { label: 'Voyageurs', value: `${dossier.voyageurs?.length || 0} pers.` },
                        { label: 'Services', value: `${dossier.prestations?.length || 0} prest.` },
                        { label: 'Statut', value: dossier.statut },
                    ].map((item) => (
                        <Box key={item.label}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{item.value}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* 2. Voyageurs (Grid Auto-fit) */}
            <Box sx={{ borderRadius: '20px', bgcolor: '#f7f5f0', p: 3, mb: 3 }}>
                <Typography variant="overline" sx={{ fontWeight: 800, color: 'rgba(0,0,0,0.4)', mb: 2, display: 'block' }}>Passagers</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(240px, 1fr))' }, gap: 2 }}>
                    {dossier.voyageurs?.map((v) => (
                        <Stack key={v.id} direction="row" spacing={1.5} sx={{ p: 2, bgcolor: 'white', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)' }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(26,26,46,0.05)', color: '#1a1a2e' }}><Person fontSize="small" /></Avatar>
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{v.prenom} {v.nom}</Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Passeport: {v.numeroPasseport || '—'}</Typography>
                            </Box>
                        </Stack>
                    ))}
                </Box>
            </Box>

            {/* 3. Prestations */}
            <Stack spacing={2} sx={{ mb: 4 }}>
                {dossier.prestations?.map((p) => <PrestationCard key={p.id} prestation={p} />)}
            </Stack>

            {/* 4. Footer Total (Responsive Stack) */}
            <Box sx={{
                borderRadius: '24px',
                background: 'linear-gradient(135deg, #c9a84c 0%, #e2c46e 50%, #c9a84c 100%)',
                p: { xs: 3, md: 4 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                boxShadow: '0 10px 30px rgba(201,168,76,0.2)'
            }}>
                <Box>
                    <Typography sx={{ color: 'rgba(26,26,46,0.5)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Estimation Finale en FCFA</Typography>
                    <Typography sx={{ color: '#1a1a2e', fontSize: '0.85rem', fontWeight: 600 }}>Montant total TTC à régler par le client</Typography>
                </Box>
                <Typography sx={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontWeight: 700,
                    fontSize: { xs: '1.6rem', md: '2rem' },
                    color: '#1a1a2e',
                    lineHeight: 1
                }}>
                    {dossier.prixTotal?.toLocaleString()}
                </Typography>
            </Box>
        </Box>
    );
}